import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { getSurfaceBorder, getSurfaceColor, radii } from '../../theme/tokens';
import { formatNetwork } from './formatters';
import type { SearchParams } from './api';

const NETWORKS = ['twitter', 'instagram', 'facebook', 'linkedin', 'youtube', 'tiktok'];

type SearchMode = 'handle' | 'profileId';

export const ProfileLinkingSearch = ({
  authorized,
  loading,
  onSearch,
}: {
  authorized: boolean;
  loading: boolean;
  onSearch: (params: SearchParams) => void;
}) => {
  const theme = useTheme();
  const [network, setNetwork] = useState(NETWORKS[0]);
  const [mode, setMode] = useState<SearchMode>('handle');
  const [value, setValue] = useState('');

  const canSubmit = authorized && value.trim().length > 0 && !loading;

  const submit = () => {
    if (!canSubmit) return;
    onSearch({ network, [mode]: value.trim() } as SearchParams);
  };

  return (
    <Box
      sx={{
        backgroundColor: getSurfaceColor(theme, 'raised'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.xl}px`,
        p: { md: 2.5, xs: 2 },
      }}
    >
      <Stack direction={{ md: 'row', xs: 'column' }} spacing={1.25}>
        <Select
          value={network}
          onChange={e => setNetwork(e.target.value)}
          size="small"
          sx={{
            backgroundColor: getSurfaceColor(theme, 'subtle'),
            minWidth: { md: 160 },
          }}
        >
          {NETWORKS.map(n => (
            <MenuItem key={n} value={n}>
              {formatNetwork(n)}
            </MenuItem>
          ))}
        </Select>

        <Stack direction="row" spacing={0.75}>
          {(['handle', 'profileId'] as SearchMode[]).map(m => (
            <ButtonBase
              key={m}
              onClick={() => setMode(m)}
              sx={{
                backgroundColor:
                  mode === m ? alpha(theme.palette.primary.main, 0.12) : getSurfaceColor(theme, 'subtle'),
                border: `1px solid ${mode === m ? alpha(theme.palette.primary.main, 0.32) : getSurfaceBorder(theme)}`,
                borderRadius: 999,
                color: mode === m ? theme.palette.primary.main : theme.palette.text.primary,
                fontSize: 12.5,
                fontWeight: mode === m ? 700 : 600,
                minHeight: 36,
                px: 1.5,
                whiteSpace: 'nowrap',
              }}
            >
              {m === 'handle' ? 'Handle' : 'Profile ID'}
            </ButtonBase>
          ))}
        </Stack>

        <OutlinedInput
          disabled={!authorized}
          fullWidth
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') submit();
          }}
          placeholder={mode === 'handle' ? 'e.g. alyankovic' : 'e.g. 22461427'}
          size="small"
          startAdornment={<SearchOutlinedIcon fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />}
          sx={{ backgroundColor: getSurfaceColor(theme, 'subtle'), flex: 1, minWidth: 0 }}
          value={value}
        />

        <Button disabled={!canSubmit} onClick={submit} variant="contained">
          Search
        </Button>
      </Stack>

      <Typography sx={{ color: theme.palette.text.secondary, fontSize: 11.5, mt: 1 }}>
        Search by exact network handle or native profile ID — free-text name search isn't supported.
      </Typography>
    </Box>
  );
};
